
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import vuetifyToast from "vuetify-toast";
import "./BookingEditor.scss";
import DateTimePicker from "@/components/DatetimePicker.vue";
import PlacePicker from "@/components/PlacePicker.vue";
import BookingProductListItem from "./BookingProductListItem.vue";
import Booking from "@/components/Booking/models/Booking";
import moment from "moment";
import { RolePermission } from "@/models/Role";
import IPriceEstimateRequest from "../models/IPriceEstimateRequest";
import IPriceEstimate from "../models/IPriceEstimate";
import IPriceEstimateProductViewModel from "../models/IPriceEstimateProductViewModel";
import Routes from "../../../Routes";
import Passenger from "../../Passenger/models/Passenger";
import Destination from "../../Passenger/models/Destination";
import IGooglePlaceCachedPrediction from "../models/IGooglePlaceCachedPrediction";
import IMovitBookingAddressViewModel from "../models/IMovitBookingAddressViewModel";
import IBookingAddressViewModel from "../models/IBookingAddressViewModel";
import IMovitPlace from "../models/IMovitPlace";
import BookingPtpSelect from '@/components/Booking/components/BookingPtpSelect.vue';
import TransportationPermissionInfo from '@/components/Passenger/models/TransportationPermissionInfo';
import MovitInstanceConfig from "@/models/MovitInstanceConfig";
import { IPassengerSearchResult } from "@/typings/timit";
import restService from "@/services/restService"

@Component({
  components: {
    BookingProductListItem,
    BookingPtpSelect,
    DateTimePicker,
    PlacePicker,
  }
})
export default class BookingEditor extends Vue {
  static EVENT_BOOKING_EDITOR_OPEN = "EVENT_BOOKING_EDITOR_OPEN";
  static EVENT_BOOKING_EDITOR_CLOSE = "EVENT_BOOKING_EDITOR_CLOSE";

  searchDebounceTimeout: any = null;
  searchDebounceTimeoutDurationMs: number = 1500;

  show: boolean = false;
  resetTimeout: any = null;
  showEditor: boolean = false;
  editedBooking: Booking = null;
  // TODO: This property should probably come from configuration
  allowDropoffTime = true;
  isDropoffTime: boolean = false;
  valid: boolean = true;
  saving: boolean = false;
  deleting: boolean = false;
  priceEstimate: IPriceEstimate = null;
  selectedProduct: IPriceEstimateProductViewModel = null;
  selectedPtp: number | null = null;
  contactPhoneNumber: string = '';
  step: number = 1;
  loading: boolean = false;
  $confirm: any;
  $t: any;

  // holding passenger places from movit
  passengerBookingPlacesInfo: any = null;

  passengerSearchInput: string = "";
  prevPassengerSearchInput: string = "";
  searchInProgress: boolean = false;

  passengerSearchResults: IPassengerSearchResult[] = [];

  get storeMovitInstanceConfigs(): MovitInstanceConfig[] {
    return this.$store.state.app.movitInstanceConfigs as MovitInstanceConfig[];
  }

  get destinationMovitInstanceConfig(): MovitInstanceConfig | undefined {
    return this.storeMovitInstanceConfigs.find(
      (mic) => mic.instanceId === this.destination.movitInstanceId
    );
  }

  get requirements() {
    return this.destinationMovitInstanceConfig.requirements
      ? this.destinationMovitInstanceConfig.requirements
      : [];
  }

  @Watch("passengerSearchInput")
  async onPassengerSearchInputChanged(val: string, oldVal: string) {
    if (val) {
      this.prevPassengerSearchInput = val;
      await this.searchPassengers(val);
    }
  }

  async searchPassengers(search: string) {
    if (this.searchDebounceTimeout){
      clearTimeout(this.searchDebounceTimeout);
    }

    this.searchInProgress = true;
    this.searchDebounceTimeout = setTimeout(
      async () => {
        if (search && search.length > 0) {
          const searchParams = { destinationId: this.routeDestinationId};
          const result: IPassengerSearchResult[] = await restService.passengers.searchPassengers(search, searchParams);
          this.searchInProgress = false;
          const destinations: Destination[] = this.$store.state.destination.destinations;
          this.passengerSearchResults = result
            .map(p => {
              const destination = destinations.find(d => d.id === p.destinationId);
              p.destinationName = destination && destination.alias ? destination.alias : '';
              p.movitInstanceName = destination && destination.movitInstanceName ? destination.movitInstanceName : '';
              return p;
            })
        }
      },
      this.searchDebounceTimeoutDurationMs
    )
  }

  startDateString: string = null;
  @Watch("startDateString") onStartDateStringChanged(val, oldVal) {
    if (this.editedBooking) {
      this.editedBooking.startDateString = val;
    }
  }

  startTimeString: string = null;
  @Watch("startTimeString") onStartTimeStringChanged(val, oldVal) {
    if (this.editedBooking) {
      this.editedBooking.startTimeString = val;
    }
  }

  @Watch("show") onShowChanged(val, oldVal) {
    if (val) {
      if (this.resetTimeout) {
        clearTimeout(this.resetTimeout);
      }
    } else {
      // Reset to empty after a small delay (after close transition)
      this.resetTimeout = setTimeout(() => {
        this.startDateString = null;
        this.startTimeString = null;
        this.editedBooking = null;
        this.priceEstimate = null;
        this.selectedProduct = null;
        this.step = 1;
      }, 500);
    }
    if (!val && this.$route.name === Routes.bookingListCreateNewRoute.name) {
      this.$router.push({
        name: Routes.bookingListWithStateRoute.name,
        params: {
          state: this.routeStateNameLowerCase
        }
      });
    }
    this.showEditor = val;
  }

  get isUnableToCreateBooking() {
    return !this.selectedProduct && !this.editedBooking.transportationPermission;
  }

  get isTransportationPermissionEnabled(): boolean {
    return this.$store.getters['auth/hasTransportationPermission']([RolePermission.ReadTransportationPermissions]);
  }

  fetchPassengerBookingPlacesInfoFromStore() {
    const info = this.$store.state.passenger.passengerPlacesInfo as IMovitBookingAddressViewModel;
    const gInfo = this.convertMovitBookingAddressInfoToGoogle(info);
    this.passengerBookingPlacesInfo = gInfo;
  }

  adhocPassenger: IPassengerSearchResult = {
    name: "NEW_CREATOR_ITEM",
    id: Math.round(9000000 + Math.random() * 1000000),
    creator: true
  } as any;

  get passengerOptions(): IPassengerSearchResult[] {
    let options = this.passengerSearchResults
      .filter(p => !p.inactive)
    if (this.passengerSearchInput &&
      this.passengerSearchInput.length > 2 &&
      this.passengerSearchInput.includes(" ") &&
      !this.passengerSearchInput.startsWith(" ") &&
      !this.passengerSearchInput.endsWith(" ")) {
      options.push(this.adhocPassenger);
    }
    return options;
  }

  passengerSelectFilter(item, queryText, itemText) {
    const itemName = item.name.toLowerCase();
    const searchText = queryText.toLowerCase();
    return (
      itemName.indexOf(searchText) > -1 ||
      (item.creator &&
        !this.passengerOptions.find(
          g => g.name.toLowerCase() === this.passengerSearchInput.toLowerCase()
        ))
    );
  }

  selectedPassengerId: number = null;
  @Watch("selectedPassengerId") async onSelectedPassengerChanged(val, oldVal) {
    if (val) {
      this.adhocPassenger.name = this.prevPassengerSearchInput;
      if (val != this.adhocPassenger.id){
        await this.$store.dispatch("passenger/getPassengerById", {params: { id: val}});
        this.fetchPassengerBookingPlacesInfo(val);

        if (this.isTransportationPermissionEnabled) {
          await this.fetchPassengerTransportationPermissionInfo(val);
        }
      }
    }
  }

  get storePassenger(): Passenger {
    return this.$store.state.passenger.passenger;
  }

  get selectedPassenger(): Passenger | null {
    return this.storePassenger && this.storePassenger.id === this.selectedPassengerId
      ? this.storePassenger
      : this.adhocPassenger.id == this.selectedPassengerId
        ? this.adhocPassenger as unknown as Passenger
        : null;
  }

  get storeBookings(): Booking[] {
    return this.$store.state.booking.bookings;
  }

  get bookingIsLocked() {
    return this.editedBooking.isLocked;
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  get destination(): Destination {
    return this.$store.state.destination.destinations.find(d => d.id === this.routeDestinationId);
  }

  get me() {
    return this.$store.state.user.me;
  }

  get userCanDeleteBookings(): boolean {
    return this.me ? this.me.hasPermissions(this.routeDestinationId, [RolePermission.DeleteBookings]) : false;
  }

  get showWeekends(): boolean {
    return this.$store.state.user.showWeekends;
  }

  get minStartDateString(): string {
    const tomorrowMoment = moment().startOf("day");
    return tomorrowMoment.format("YYYY-MM-DD");
  }

  get startDateLabel() {
    return this.isDropoffTime ? this.$t('generic.dropoffDate') : this.$t('generic.pickupDate');
  }

  get startTimeLabel() {
    return this.isDropoffTime ? this.$t('generic.dropoffTime') : this.$t('generic.pickupTime');
  }

  get startDateTitle() {
    return this.isDropoffTime ? this.$t('generic.dropoffDateSelect') : this.$t('generic.pickupDateSelect');
  }

  get startTimeTitle() {
    return this.isDropoffTime ? this.$t('generic.dropoffTimeSelect') : this.$t('generic.pickupTimeSelect');
  }

  get transportationPermissions() {
    if (!this.selectedPassengerId) {
      return [];
    }

    const transportationPermissionInfo: TransportationPermissionInfo =
      this.$store.state.passenger.transportationPermissionInfos
        .find((tpi: TransportationPermissionInfo) => tpi.timitPassengerId === this.selectedPassengerId);

    if (!transportationPermissionInfo) {
      return [];
    }

    const bookingDate = new Date(this.editedBooking.startDate).getTime();
    const permissions = transportationPermissionInfo.permissions.filter(ptp => {
      if (!ptp.externalId) {
        return false;
      }
      const startDate = new Date(ptp.startDate).getTime();
      const endDate = new Date(ptp.endDate).getTime();
      return startDate <= bookingDate && bookingDate <= endDate;
    });

    return permissions;
  }

  @Watch("$route", { immediate: true })
  onRouteChanged(val, oldVal) {
    if (val.name === Routes.bookingListCreateNewRoute.name) {
      this.openNewBookingDialog();
    } else if (
      oldVal &&
      oldVal.name === Routes.bookingListCreateNewRoute.name
    ) {
      this.closeNewBookingDialog();
    }
  }

  closeNewBookingDialog() {
    this.$root.$emit(BookingEditor.EVENT_BOOKING_EDITOR_CLOSE);
  }

  openNewBookingDialog() {
    const destinationId: number = this.routeDestinationId;

    // When Show weekends is on
    // - startDate always next day
    // When Hide weekends is on
    // - startDate monday if current day is fri(5), sat(6), or sun (0 and 7)
    // Both cases
    // - endDate always same as startDate day

    const startDateMoment = moment().add(15, "minutes");
    const remainder = 10 - (startDateMoment.minute() % 10);
    const startDate = moment(startDateMoment)
      .add(remainder, "minutes")
      .toDate();

    const newBooking: Booking = new Booking();
    newBooking.startDate = startDate;
    newBooking.destinationId = this.destination.id;
    newBooking.createdByEmail = this.me.email;
    newBooking.creating = true;
    this.onEditBooking(newBooking);
  }

  created() {
    (window as any).__beditor = this;
    this.$root.$on(BookingEditor.EVENT_BOOKING_EDITOR_OPEN, this.onEditBooking);
    this.$root.$on(
      BookingEditor.EVENT_BOOKING_EDITOR_CLOSE,
      this.onCloseEditorEvent
    );
  }

  onCloseEditorEvent() {
    this.show = false;
  }

  onEditBooking(booking: Booking) {
    this.editedBooking = Booking.CopyFrom(booking);
    this.startDateString = this.editedBooking.startDateString;
    this.startTimeString = this.editedBooking.startTimeString;
    this.show = true;
  }

  async onPassengerSelect(passengerSearchResult: IPassengerSearchResult) {
    if (passengerSearchResult){
      await this.$store.dispatch("passenger/getPassengerById", {params: { id:  passengerSearchResult.id}});
    }
    this.selectedPassengerId = passengerSearchResult.id;
  }

  async onStartDateStringUpdated(val, oldVal) {
    if (!this.startDateString) {
      return;
    }
    this.startDateString = val;
  }

  async onStartTimeStringUpdated(val, oldVal) {
    if (!this.startTimeString) {
      return;
    }
    this.startTimeString = val;
  }

  async onDeleteClick() {
    this.deleting = true;
    // TODO: Check that booking state allows for deletion

    // Confirm deletion
    let deleteConfirmed = false;
    try {
      await this.$confirm({
        title: this.$i18n.translate("booking.delete.confirm.dialog.title", "Deleting booking"),
        message: this.$i18n.translate("booking.delete.confirm.dialog.message", "Do you really want to delete the booking?"),
        okButton: this.$i18n.translate("booking.delete.confirm.dialog.yes", "Yes"),
        cancelButton: this.$i18n.translate("booking.delete.confirm.dialog.no", "No")
      });
      deleteConfirmed = true;
    } catch (err) {
      // Confirm deletion was answered "No", we are expecting the confirm dialog to throw an error
    }

    // Check if deletion was confirmed
    if (deleteConfirmed) {
      // Deletion was confirmed
      try {
        const destinationId = this.editedBooking.destinationId;
        await this.$store.dispatch("booking/deleteBooking", {
          params: {
            id: this.editedBooking.id,
            destinationId: this.editedBooking.destinationId
          }
        });
        this.show = false;
        // TODO: If booking order has been sent in the past, should we notify Movit of deletion?
      } catch (err) {
        vuetifyToast.error(
          this.$i18n.translate(
            "booking.delete.error.generic",
            "There was an error deleting the booking. Please reload the page and try again later."
          )
        );
        console.error("Error when saving passenger:", err);
      }
    }
    this.deleting = false;
  }

  async onGetPriceEstimateClick() {
    if (!this.editedBooking.fromPlace || !this.editedBooking.fromAddress) {
      return;
    }

    const booking = this.editedBooking;
    const estimateRequest: IPriceEstimateRequest = {
      startTime: booking.startDate,
      startPlace: {
        alias: booking.fromPlace.address,
        latitude: booking.fromPlace.location.lat,
        longitude: booking.fromPlace.location.lng,
        streetAddress: booking.fromPlace.address,
        placeType: 0,
        id: 0
      },
      endPlace: booking.toPlace
        ? {
            alias: booking.toPlace.address,
            latitude: booking.toPlace.location.lat,
            longitude: booking.toPlace.location.lng,
            streetAddress: booking.toPlace.address,
            placeType: 0,
            id: 0
          }
        : null
    };
    this.loading = true;
    try {
      const result = await this.$store.dispatch("booking/getPriceEstimate", {
        params: {
          destinationId: this.routeDestinationId
        },
        data: estimateRequest
      });
      this.priceEstimate = result.data;
      this.step++;
    } catch (ex) {
      let message = (ex as any).message;
      if (!message) {
        message = this.$i18n.translate(
          "booking.errorWhileRetrievingInfo",
          "An error occurred while retrieving information. Please try again later or contact support."
        );
      }
      vuetifyToast.error(message);
      console.error(ex);
    }
    this.loading = false;
  }

  onProductSelected(product: IPriceEstimateProductViewModel) {
    this.selectedProduct = product;
    this.step++;
  }

  onProductDeselected(product: IPriceEstimateProductViewModel) {
    this.selectedProduct = null;
  }

  async onSubmit() {
    // TODO: Re-enable form, prevent enter key and re-enable validation
    this.saveBooking();
  }

  async saveBooking() {
    this.saving = true;
    try {
      if (this.editedBooking.creating) {
        // Make sure we don't have id set to null, but rather undefined
        delete this.editedBooking.id;
        // Create
        const response = await this.$store.dispatch("booking/createBooking", {
          params: {
            destinationId: this.routeDestinationId
          },
          data: Booking.toApiBookingRequestViewModel(
            this.editedBooking,
            this.priceEstimate.route,
            this.selectedProduct,
            this.selectedPassenger ||
              this.selectedPassenger.name ||
              this.me.email,
              this.contactPhoneNumber,
            this.isDropoffTime
          )
        });

        const newBookingId = response.data.id;
      } else {
        // Update
        await this.$store.dispatch("booking/updateBooking", {
          params: {
            id: this.editedBooking.id,
            destinationId: this.editedBooking.destinationId
          },
          data: Booking.toApiBookingUpdateRequestViewModel(this.editedBooking)
        });
      }
    } catch (ex) {
      vuetifyToast.error(
        this.$i18n.translate(
          "booking.edit.save.error",
          "Something went wrong when saving the booking. Please try again later."
        )
      );
      throw ex;
    } finally {
      this.saving = false;
    }
    this.show = false;
  }

  get routeStateNameLowerCase(): string {
    return this.$store.state.route.params.state;
  }

  onCloseClick() {
    this.show = false;
  }

  beforeDestroy() {
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
    }
    this.$root.$off(
      BookingEditor.EVENT_BOOKING_EDITOR_OPEN,
      this.onEditBooking
    );
  }

  async fetchPassengerBookingPlacesInfo(selectedPassId: number) {

    const places = await this.$store
      .dispatch("passenger/getPassengerPlaces", {
        params: {
          passengerId: selectedPassId
        }
      })
      .then(val => {
        const test = val as IMovitBookingAddressViewModel;

        this.fetchPassengerBookingPlacesInfoFromStore();
      });
  }

  async fetchPassengerTransportationPermissionInfo(passengerId: number) {
    await this.$store.dispatch('passenger/getPassengerTransportationPermissionInfo', {params: { passengerId }});
  }

  convertMovitBookingAddressInfoToGoogle(
    bookingAddressInfo: IMovitBookingAddressViewModel
  ): IBookingAddressViewModel {
    if (bookingAddressInfo.allowFreeAddresses) {
      return null;
    }
    const result = {
      allowFreeAddresses: bookingAddressInfo.allowFreeAddresses,
      addresses: []
    } as IBookingAddressViewModel;

    bookingAddressInfo.addresses.forEach(element => {
      result.addresses.push(this.convertToGoogleAddress(element));
    });
    return result;
  }

  convertToGoogleAddress(
    movitPlace: IMovitPlace
  ): IGooglePlaceCachedPrediction {
    const result = {
      prediction: {
        description: movitPlace.streetAddress + ", " + movitPlace.city
      },
      result: {
        address_components: [
          {
            long_name: movitPlace.streetAddress,
            short_name: movitPlace.streetAddress,
            types: ["route"]
          },
          {
            long_name: movitPlace.city,
            short_name: movitPlace.city,
            types: ["locality", "political"]
          },
          {
            long_name: movitPlace.postalcode,
            short_name: movitPlace.postalcode,
            types: ["postal_code"]
          },
          {
            long_name: "Suomi",
            short_name: "FI",
            types: ["country", "political"]
          }
        ],
        formatted_address: movitPlace.streetAddress + " " + movitPlace.city,
        geometry: {
          location: new google.maps.LatLng(
            movitPlace.latitude,
            movitPlace.longitude
          )
        },
        name: movitPlace.streetAddress,
        types: ["street_address"],
        vicinity: movitPlace.city
      }
    } as IGooglePlaceCachedPrediction;
    return result;
  }
}
